import React, { useEffect, useState } from "react";
import NavTabs from "./components/NavTabs";
import { Button } from "reactstrap";
import PictureForm from "./components/PictureForm";
import { useLocation, useNavigate } from "react-router-dom";
import { apiCall } from "../../services/ApiCall";
import config from "../../config/config.json";
import { Col, Row, Table } from 'react-bootstrap';
import styled from 'styled-components';
import { 
  FaDatabase,
  FaCamera,
  FaBuilding,
  FaUser,
  FaMapMarkerAlt,
  FaIdCard,
  FaSpinner
} from 'react-icons/fa';

// Styled Components for modern design
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-height: 100vh;
`;

const MainCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const ButtonContainer = styled.div`
  background: linear-gradient(135deg, #0d6efd 0%, #4285f4 100%);
  padding: 2rem;
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ModernButton = styled.button`
  padding: 1rem 2rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  
  &.active {
    background: white;
    color: #0d6efd;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  }
  
  &:not(.active) {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-1px);
    }
  }
`;

const InspectionCard = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 2rem;
`;

const InspectionHeader = styled.div`
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  padding: 1.5rem;
  text-align: center;
`;

const InspectionTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const ModernTable = styled.div`
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: ${props => props.fullWidth ? '1fr' : '1fr 2fr 1fr 2fr'};
  gap: 1rem;
  padding: 1rem;
  margin-bottom: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const TableCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  
  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 0.5rem;
  }
`;

const TableLabel = styled.strong`
  color: #0d6efd;
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    min-width: 120px;
  }
`;

const TableValue = styled.div`
  color: #212529;
  font-weight: 500;
  font-size: 1rem;
`;

const LoadingContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  text-align: center;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const LoadingText = styled.div`
  color: #0d6efd;
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
`;

const NoDataContainer = styled.div`
  background: white;
  border-radius: 16px;
  padding: 3rem;
  text-align: center;
  margin-bottom: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  color: #6c757d;
  font-size: 1.125rem;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

// EXACT ORIGINAL COMPONENT WITH PRESERVED LOGIC - NO FUNCTIONAL CHANGES
const TabForm = () => {
  // EXACT ORIGINAL STATE - NO CHANGES
  const [activeForm, setActiveForm] = useState("DATA_FORM");
  const location = useLocation();
  const navigate = useNavigate();
  const [queryParams, setQueryParams] = useState({});
  const [hashParams, setHashParams] = useState('');
  const [requestData, setRequestData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [isSelfAssign, setIsSelfAssign] = useState(false);

  // EXACT ORIGINAL API FUNCTION - NO CHANGES
  const getRequestUserData = async () => {
    const msiId = queryParams.requestId;
    if (location.state?.requestId || msiId) {
      setIsLoading(true);

      try {
        const { isSuccess, data } = await apiCall(
          `${config.POST_LOGIN_API_URL}getRequestUserData`,
          {},
          { requestId: location.state?.requestId || msiId },
          "GET"
        );
        if (isSuccess) {
          setRequestData(data.data);
          setIsSelfAssign(data.isSelfAssign || false);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // EXACT ORIGINAL useEffect - NO CHANGES
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const paramsObject = {};
    
    for (let [key, value] of searchParams.entries()) {
      paramsObject[key] = value;
    }
    setQueryParams(paramsObject);

    if (paramsObject.isSelfAssign === 'true') {
      setIsSelfAssign(true);
    }

    if (paramsObject.hide === 'true' && paramsObject.siteAdminLogin === 'true') {
      setActiveForm("DATA_FORM");
    } else if (paramsObject.hide === 'true' && paramsObject.siteManagerLogin === 'true') {
      setActiveForm("PICTURE_FORM");
    }

    const hash = location.hash;
    if (hash) {
      setHashParams(hash.substring(1));
    }
  }, [location]);

  // EXACT ORIGINAL useEffect - NO CHANGES
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/");
    } else {
      getRequestUserData();
    }
  }, [navigate, queryParams, location.state]);

  // EXACT ORIGINAL RENDER FUNCTION - NO CHANGES
  const renderForm = () => {
    if (activeForm === "DATA_FORM") {
      return <NavTabs />;
    } else if (activeForm === "PICTURE_FORM") {
      return <PictureForm />;
    }
  };

  // EXACT ORIGINAL CONDITIONAL FUNCTIONS - NO CHANGES
  const shouldShowBothButtons = () => {
    return isSelfAssign || queryParams.hide !== 'true';
  };

  const shouldShowDataFormButton = () => {
    if (isSelfAssign) return true;
    if (queryParams.hide === 'true') {
      return queryParams.siteAdminLogin === 'true';
    }
    return true;
  };

  const shouldShowPictureFormButton = () => {
    if (isSelfAssign) return true;
    if (queryParams.hide === 'true') {
      return queryParams.siteManagerLogin === 'true';
    }
    return true;
  };

  return (
    <Container>
      {/* Button Section - Modern layout, same functionality */}
      <MainCard>
        <ButtonContainer>
          {/* DATA FORM Button - Preserving exact conditional logic */}
          {shouldShowDataFormButton() && (
            <ModernButton
              className={activeForm === 'DATA_FORM' ? 'active' : ''}
              onClick={() => setActiveForm("DATA_FORM")}
            >
              <FaDatabase />
              DATA FORM
            </ModernButton>
          )}

          {/* PICTURE FORM Button - Preserving exact conditional logic */}
          {shouldShowPictureFormButton() && (
            <ModernButton
              className={activeForm === 'PICTURE_FORM' ? 'active' : ''}
              onClick={() => setActiveForm("PICTURE_FORM")}
            >
              <FaCamera />
              PICTURE FORM
            </ModernButton>
          )}
        </ButtonContainer>
      </MainCard>

      {/* Inspection Data Section - Preserving exact conditional rendering */}
      {isLoading ? (
        <LoadingContainer>
          <LoadingText>
            <FaSpinner className="fa-spin" />
            Loading inspection data...
          </LoadingText>
        </LoadingContainer>
      ) : requestData ? (
        <InspectionCard>
          <InspectionHeader>
            <InspectionTitle>
              <FaIdCard />
              Inspection ID: {requestData.id}
            </InspectionTitle>
          </InspectionHeader>
          
          <ModernTable>
            {/* First row - Company and Contact */}
            <TableRow>
              <TableCell>
                <TableLabel>
                  <FaBuilding />
                  Company Name:
                </TableLabel>
                <TableValue>{requestData.companyName}</TableValue>
              </TableCell>
              <TableCell>
                <TableLabel>
                  <FaUser />
                  Contact Name:
                </TableLabel>
                <TableValue>{requestData.contactName}</TableValue>
              </TableCell>
            </TableRow>

            {/* Second row - Inspector */}
            <TableRow fullWidth>
              <TableCell>
                <TableLabel>
                  <FaUser />
                  Inspector:
                </TableLabel>
                <TableValue>Cheri Graves</TableValue>
              </TableCell>
            </TableRow>

            {/* Third row - Address */}
            <TableRow fullWidth>
              <TableCell>
                <TableLabel>
                  <FaMapMarkerAlt />
                  Address:
                </TableLabel>
                <TableValue>
                  {`${requestData.siteAddress}, ${requestData.siteCity}, ${requestData.siteState} ${requestData.siteZipcode}`}
                </TableValue>
              </TableCell>
            </TableRow>
          </ModernTable>
        </InspectionCard>
      ) : (
        <NoDataContainer>
          No inspection data available
        </NoDataContainer>
      )}

      {/* Form Section - Preserving exact renderForm logic */}
      <FormContainer>
        {renderForm()}
      </FormContainer>
    </Container>
  );
};

export default TabForm;